import {
  Component,
  Suspense,
  lazy,
  useState,
  type ComponentType,
  type ReactNode,
} from "react";
import { RotateCw } from "lucide-react";
import { ChromeIconButton, Tooltip } from "@playbookai/ui";
import type { KnowledgeScreenContext } from "@playbookai/knowledge-studio-feature";

interface KnowledgeHelpProps {
  readonly canViewAdmin: boolean;
  readonly pathname: string;
  readonly context?: KnowledgeScreenContext;
}

type HelpLoader = () => Promise<{ default: ComponentType<KnowledgeHelpProps> }>;

const loadKnowledgeHelp: HelpLoader = async () => {
  const feature = await import("@playbookai/knowledge-studio-feature");
  return { default: feature.KnowledgeHelp };
};

interface HelpBoundaryProps {
  readonly children: ReactNode;
  readonly onRetry: () => void;
}

class KnowledgeHelpBoundary extends Component<
  HelpBoundaryProps,
  { readonly failed: boolean }
> {
  public override state = { failed: false };

  public static getDerivedStateFromError() {
    return { failed: true };
  }

  public override render(): ReactNode {
    if (!this.state.failed) return this.props.children;
    return (
      <>
        <span role="alert" className="sr-only">
          Screen help couldn’t load.
        </span>
        <Tooltip content="Help couldn’t load. Try again." describeChild={false}>
          <ChromeIconButton
            variant="bar"
            aria-label="Retry screen help"
            onClick={this.props.onRetry}
          >
            <RotateCw aria-hidden="true" className="h-5 w-5 stroke-[1.5]" />
          </ChromeIconButton>
        </Tooltip>
      </>
    );
  }
}

/** An optional help download must never replace the active workspace or its draft. */
export function KnowledgeHelpHost({
  loadHelp = loadKnowledgeHelp,
  ...props
}: KnowledgeHelpProps & { readonly loadHelp?: HelpLoader }): JSX.Element {
  const [request, setRequest] = useState(() => ({
    generation: 0,
    Help: lazy(loadHelp),
  }));
  // React.lazy retains a rejected promise. Retrying needs a new lazy identity
  // as well as a fresh boundary, rather than merely clearing the error flag.
  const { generation, Help } = request;
  return (
    <KnowledgeHelpBoundary
      key={generation}
      onRetry={() =>
        setRequest((current) => ({
          generation: current.generation + 1,
          Help: lazy(loadHelp),
        }))
      }
    >
      <Suspense
        fallback={
          <span className="inline-block h-8 w-8 shrink-0">
            <span
              role="status"
              aria-label="Loading screen help"
              className="sr-only"
            >
              Loading screen help…
            </span>
          </span>
        }
      >
        <Help {...props} />
      </Suspense>
    </KnowledgeHelpBoundary>
  );
}
